import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner, Table, SortableTh } from '@/components/ui';
import { Task } from '../../../types/Task';
import { Project } from '../../../types/Project';
import { Effort } from '../../../types/Effort';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import { formatStatusForDisplay } from '../../../utils/statusUtils';
import InfoTooltip from '../../../components/InfoTooltip';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ResizeHandle } from '../../../components/ResizeHandle';
import { TaskActionsDropdown } from './ProjectTasksTab';

interface ProjectWorksTableProps {
  tasks: Task[];
  loading?: boolean;
  canEditTask: boolean;
  canCloneTask: boolean;
  canCloseTask: boolean;
  canReopenTask: boolean;
  canCancelTask: boolean;
  canToggleVehicleFuel: boolean;
  onViewHistory: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onCloneTask: (task: Task) => void;
  onCloseTask: (task: Task) => void;
  onReopenTask: (task: Task) => void;
  onCancelTask: (task: Task) => void;
  onPrintPDF: (task: Task) => void;
  onConvertToQuantified?: (task: Task) => void;
  onConvertToCustom?: (task: Task) => void;
  onReportAccomplishment?: (task: Task) => void;
  onSetInternalReview?: (task: Task) => void;
  onSetActive?: (task: Task) => void;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
  /** When set, clicking a task row (or task number) opens the task detail modal */
  onTaskClick?: (task: Task) => void;
  /**
   * Controlled (server-side) sorting. Pass all three to have the parent own the sort and refetch
   * — required whenever `tasks` is one page of a server-paginated list, because sorting locally
   * would only reorder the rows currently on screen. Omit them for a fully client-side list.
   */
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
}

const ProjectWorksTable: React.FC<ProjectWorksTableProps> = ({
  tasks,
  loading = false,
  canEditTask,
  canCloneTask,
  canCloseTask,
  canReopenTask,
  canCancelTask,
  canToggleVehicleFuel,
  onViewHistory,
  onEditTask,
  onCloneTask,
  onCloseTask,
  onReopenTask,
  onCancelTask,
  onPrintPDF,
  onConvertToQuantified,
  onConvertToCustom,
  onReportAccomplishment,
  onSetInternalReview,
  onSetActive,
  onVehicleFuelEffortPatched,
  onTaskClick,
  sortField: controlledSortField,
  sortDirection: controlledSortDirection,
  onSort: controlledOnSort
}) => {
  const handleRowClick = (task: Task, event: React.MouseEvent) => {
    if (!onTaskClick) return;
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, .dropdown-menu, .dropdown-item')) return;
    onTaskClick(task);
  };

  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const isServerSorted = !!controlledOnSort;
  const [localSortField, setLocalSortField] = useState<string | null>('id');
  const [localSortDirection, setLocalSortDirection] = useState<'asc' | 'desc'>('desc');
  const sortField = isServerSorted ? controlledSortField ?? null : localSortField;
  const sortDirection = isServerSorted ? controlledSortDirection ?? 'desc' : localSortDirection;

  const RESIZABLE_COLUMN_IDS = [
    'taskNumber',
    'name',
    'project',
    'status',
    'type',
    'coding',
    'activity',
    'actions'
  ];
  const defaultWidths: Record<string, string> = {
    project: '200px',
    taskNumber: '120px',
    name: '280px',
    status: '120px',
    type: '110px',
    coding: '120px',
    activity: '200px',
    actions: '100px'
  };
  const { getColumnWidth, getColumnWidthPx, startResize } = useResizableColumns(
    RESIZABLE_COLUMN_IDS,
    { defaultWidths }
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const isScrollingSyncRef = useRef(false);
  const scrollSyncRafRef = useRef<number | null>(null);
  const lastScrollSourceRef = useRef<'table' | 'bar' | null>(null);
  const [stickyScrollWidth, setStickyScrollWidth] = useState(0);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    // The shared <Table> primitive isn't a forwardRef component, so the table element itself
    // isn't reachable via ref — it's the wrapper's only child, which is equivalent for sizing.
    const table = wrapper?.firstElementChild;
    if (!wrapper || !table) return;
    const updateStickyWidth = () => setStickyScrollWidth(wrapper.scrollWidth);
    updateStickyWidth();
    const ro = new ResizeObserver(updateStickyWidth);
    ro.observe(table);
    return () => ro.disconnect();
  }, [tasks.length, getColumnWidth]);

  const runScrollSync = useCallback(() => {
    const src = lastScrollSourceRef.current;
    const tableEl = tableWrapperRef.current;
    const barEl = stickyScrollbarRef.current;
    if (src === 'table' && barEl && tableEl) {
      isScrollingSyncRef.current = true;
      barEl.scrollLeft = tableEl.scrollLeft;
      requestAnimationFrame(() => {
        isScrollingSyncRef.current = false;
      });
    } else if (src === 'bar' && tableEl && barEl) {
      isScrollingSyncRef.current = true;
      tableEl.scrollLeft = barEl.scrollLeft;
      requestAnimationFrame(() => {
        isScrollingSyncRef.current = false;
      });
    }
  }, []);

  const handleTableWrapperScroll = useCallback(() => {
    if (isScrollingSyncRef.current) return;
    lastScrollSourceRef.current = 'table';
    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        runScrollSync();
      });
    }
  }, [runScrollSync]);

  const handleStickyScroll = useCallback(() => {
    if (isScrollingSyncRef.current) return;
    lastScrollSourceRef.current = 'bar';
    if (scrollSyncRafRef.current === null) {
      scrollSyncRafRef.current = requestAnimationFrame(() => {
        scrollSyncRafRef.current = null;
        runScrollSync();
      });
    }
  }, [runScrollSync]);

  // SortableTh computes the next direction itself (toggle on the active column, else asc) and
  // hands it back as `dir` — this just routes it to whichever sort is currently in charge.
  const handleSort = (field: string, dir: 'asc' | 'desc') => {
    if (isServerSorted) {
      controlledOnSort!(field, dir);
      return;
    }
    setLocalSortField(field);
    setLocalSortDirection(dir);
  };

  const sortedTasks = React.useMemo(() => {
    // Already ordered by the server across the whole result set — re-sorting the current page
    // here would be a no-op at best and, on ties, would fight the server's ordering.
    if (isServerSorted) return tasks;
    if (!sortField) return tasks;
    return [...tasks].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';
      switch (sortField) {
        case 'project':
          aValue = a.project?.projectNumber || '';
          bValue = b.project?.projectNumber || '';
          break;
        case 'taskNumber':
          aValue = a.taskNumber || '';
          bValue = b.taskNumber || '';
          break;
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [tasks, sortField, sortDirection, isServerSorted]);

  const toggleEfforts = (taskId: number) => {
    setExpandedRows((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const projectMarkups = (project?: Project) =>
    project
      ? {
          vehicleMarkup: project.vehicleMarkup,
          materialMarkup: project.materialMarkup,
          labourMarkup: project.labourMarkup,
          subcontractorMarkup: project.subcontractorMarkup
        }
      : undefined;

  if (loading && tasks.length === 0) {
    return (
      <div className="tw-text-center tw-py-5">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  return (
    <div>
      {stickyScrollWidth > 0 && (
        <div
          ref={stickyScrollbarRef}
          onScroll={handleStickyScroll}
          className="tw-hidden md:tw-block"
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: 14,
            backgroundColor: 'var(--bs-body-bg, #fff)',
            borderBottom: '1px solid rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ width: stickyScrollWidth, height: 1 }} />
        </div>
      )}
      {/* Mobile (< md): stacked cards — the wide task table doesn't fit a phone. Full
          table returns at md+. */}
      <div className="tw-flex tw-flex-col tw-gap-2 tw-p-2 md:tw-hidden">
        {sortedTasks.length === 0 && !loading ? (
          <div className="tw-py-6 tw-text-center tw-text-content-muted">
            <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
            No project tasks found
          </div>
        ) : (
          sortedTasks.map((task) => {
            const isExpanded = expandedRows.includes(task.id);
            const hasEfforts = (task.efforts?.length ?? 0) > 0;
            return (
              <div
                key={`m-${task.id}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  {onTaskClick ? (
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); onTaskClick(task); }}
                      className="tw-inline-block tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-font-normal tw-leading-none tw-text-white tw-no-underline"
                    >
                      {task.taskNumber || 'N/A'}
                    </a>
                  ) : (
                    <span className="tw-inline-block tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-font-normal tw-leading-none tw-text-white">
                      {task.taskNumber || 'N/A'}
                    </span>
                  )}
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <span
                      className={`tw-inline-block tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${
                        task.status === 'ACTIVE'
                          ? 'tw-bg-[#d1e7dd] tw-text-success tw-border-success'
                          : 'tw-bg-[#e2e3e5] tw-text-secondary tw-border-secondary'
                      }`}
                    >
                      {formatStatusForDisplay(task.status)}
                    </span>
                    <TaskActionsDropdown
                      task={task}
                      canEditTask={canEditTask}
                      canCloneTask={canCloneTask}
                      canCloseTask={canCloseTask}
                      canReopenTask={canReopenTask}
                      canCancelTask={canCancelTask}
                      onViewHistory={() => onViewHistory(task)}
                      onEditTask={() => onEditTask(task)}
                      onCloneTask={() => onCloneTask(task)}
                      onCloseTask={() => onCloseTask(task)}
                      onReopenTask={() => onReopenTask(task)}
                      onCancelTask={() => onCancelTask(task)}
                      onPrintPDF={() => onPrintPDF(task)}
                      onConvertToQuantified={
                        onConvertToQuantified ? () => onConvertToQuantified(task) : undefined
                      }
                      onConvertToCustom={onConvertToCustom ? () => onConvertToCustom(task) : undefined}
                    />
                  </div>
                </div>
                <div className="tw-mt-1 tw-break-words tw-font-medium tw-text-content">
                  {task.name || task.description || '-'}
                </div>
                <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm">
                  {task.project && (
                    <span className="tw-text-content-muted">
                      Project{' '}
                      <Link
                        to={`/projects/${task.project.id}`}
                        className="tw-font-medium tw-text-primary tw-no-underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {task.project.projectNumber || task.project.name}
                      </Link>
                    </span>
                  )}
                  <span className="tw-text-content-muted">
                    Type <span className="tw-font-medium tw-text-content">{task.type}</span>
                  </span>
                  {task.activities && task.activities.length > 0 && (
                    <span className="tw-text-content-muted">
                      Coding{' '}
                      <span className="tw-font-medium tw-text-content">{task.activities[0].activityNumber}</span>
                    </span>
                  )}
                </div>
                {hasEfforts && (
                  <>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="tw-mt-3 tw-w-full"
                      active={isExpanded}
                      onClick={() => toggleEfforts(task.id)}
                      title={isExpanded ? 'Hide Efforts' : 'Show Efforts'}
                    >
                      <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} tw-mr-1`} />
                      {isExpanded ? 'Hide efforts' : 'View efforts'}
                    </Button>
                    {isExpanded && (
                      <div className="tw-mt-2 tw-overflow-x-auto">
                        <TaskEffortsTable
                          efforts={task.efforts || []}
                          columns={{ poNumber: true, fuelSurcharge: true }}
                          projectMarkups={projectMarkups(task.project)}
                          canToggleVehicleFuel={canToggleVehicleFuel}
                          onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      <div
        ref={tableWrapperRef}
        className="tw-hidden md:tw-block tw-w-full tw-overflow-x-auto"
        onScroll={handleTableWrapperScroll}
      >
        <Table
          striped
          hover
          style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}
        >
          <thead className="table-sticky-header">
            <tr>
              <SortableTh
                label="Task Number"
                sortKey="taskNumber"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('taskNumber'), position: 'relative' }}
              >
                <ResizeHandle
                  columnId="taskNumber"
                  currentWidthPx={getColumnWidthPx('taskNumber')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Name"
                sortKey="name"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('name'), position: 'relative' }}
              >
                <ResizeHandle
                  columnId="name"
                  currentWidthPx={getColumnWidthPx('name')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Project"
                sortKey="project"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('project'), position: 'relative' }}
              >
                <ResizeHandle
                  columnId="project"
                  currentWidthPx={getColumnWidthPx('project')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Status"
                sortKey="status"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('status'), position: 'relative' }}
                className="tw-text-center"
              >
                <ResizeHandle
                  columnId="status"
                  currentWidthPx={getColumnWidthPx('status')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <SortableTh
                label="Type"
                sortKey="type"
                activeKey={sortField}
                direction={sortDirection}
                onSort={handleSort}
                style={{ width: getColumnWidth('type'), position: 'relative' }}
                className="tw-text-center"
              >
                <ResizeHandle
                  columnId="type"
                  currentWidthPx={getColumnWidthPx('type')}
                  onResizeStart={startResize}
                />
              </SortableTh>
              <th style={{ width: getColumnWidth('coding'), position: 'relative' }} className="tw-text-center">
                <InfoTooltip
                  content={
                    <div>
                      <div>
                        <strong>Coding Formula:</strong>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        If a task belongs to a project that has a job number, the task is coded to the job
                        number. Otherwise, the task is coded to its assigned activity number.
                      </div>
                    </div>
                  }
                  id="coding-tooltip-project-works"
                  placement="top"
                  maxWidth="400px"
                >
                  <span>
                    Coding <i className="bi bi-info-circle tw-ml-1" style={{ fontSize: '0.85rem', opacity: 0.7 }} />
                  </span>
                </InfoTooltip>
                <ResizeHandle
                  columnId="coding"
                  currentWidthPx={getColumnWidthPx('coding')}
                  onResizeStart={startResize}
                />
              </th>
              <th style={{ width: getColumnWidth('activity'), position: 'relative' }} className="tw-text-center">
                Activity
                <ResizeHandle
                  columnId="activity"
                  currentWidthPx={getColumnWidthPx('activity')}
                  onResizeStart={startResize}
                />
              </th>
              <th style={{ width: getColumnWidth('actions'), position: 'relative' }} className="tw-text-right">
                Actions
                <ResizeHandle
                  columnId="actions"
                  currentWidthPx={getColumnWidthPx('actions')}
                  onResizeStart={startResize}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => (
              <React.Fragment key={task.id}>
                <tr
                  className={`tw-align-middle${onTaskClick ? ' table-row-clickable' : ''}`}
                  onClick={onTaskClick ? (e) => handleRowClick(task, e) : undefined}
                  style={onTaskClick ? { cursor: 'pointer' } : undefined}
                >
                  <td>
                    {onTaskClick ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onTaskClick(task);
                        }}
                        className="tw-inline-block tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-font-normal tw-leading-none tw-text-white tw-no-underline"
                        style={{ cursor: 'pointer' }}
                      >
                        {task.taskNumber || 'N/A'}
                      </a>
                    ) : (
                      <span className="tw-inline-block tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-font-normal tw-leading-none tw-text-white">{task.taskNumber || 'N/A'}</span>
                    )}
                  </td>
                  <td className="tw-break-words">{task.name || task.description || '-'}</td>
                  <td>
                    {task.project ? (
                      <Link
                        to={`/projects/${task.project.id}`}
                        className="tw-no-underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="tw-inline-block tw-rounded tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-leading-none tw-text-primary tw-whitespace-normal tw-text-left">
                          {task.project.projectNumber || task.project.id}
                          <span className="tw-block tw-text-sm tw-text-content-muted tw-font-normal">{task.project.name}</span>
                        </span>
                      </Link>
                    ) : (
                      <span className="tw-text-content-muted">-</span>
                    )}
                  </td>
                  <td className="tw-text-center">
                    <span
                      className={`tw-inline-block tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${
                        task.status === 'ACTIVE'
                          ? 'tw-bg-[#d1e7dd] tw-text-success tw-border-success'
                          : 'tw-bg-[#e2e3e5] tw-text-secondary tw-border-secondary'
                      }`}
                    >
                      {formatStatusForDisplay(task.status)}
                    </span>
                  </td>
                  <td className="tw-text-center">
                    <span
                      className={`tw-inline-block tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none ${
                        task.type === 'Quantified'
                          ? 'tw-bg-[#cff4fc] tw-text-info tw-border-info'
                          : task.type === 'Routine'
                            ? 'tw-bg-warning tw-text-warning-text tw-border-[#ffc107]'
                            : 'tw-bg-[#ced4da] tw-text-black tw-border-content'
                      }`}
                    >
                      {task.type}
                    </span>
                  </td>
                  <td className="tw-text-center">
                    {task.project?.jobNumber ? (
                      <span className="tw-inline-block tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-bg-[#cfe2ff] tw-text-primary tw-border-primary">
                        {task.project.jobNumber}
                      </span>
                    ) : task.activities && task.activities.length > 0 ? (
                      <span className="tw-inline-block tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-bg-[#e2e3e5] tw-text-secondary tw-border-secondary">
                        {task.activities[0].activityNumber}
                      </span>
                    ) : (
                      <span className="tw-text-content-muted tw-text-sm">-</span>
                    )}
                  </td>
                  <td className="tw-text-center">
                    {task.activities && task.activities.length > 0 ? (
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        {task.activities.map((activity, index) => (
                          <span
                            key={activity.id || index}
                            className="tw-inline-block tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-bg-[#e2e3e5] tw-text-secondary tw-border-secondary"
                          >
                            {activity.activityNumber} - {activity.activityName}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="tw-text-content-muted tw-text-sm">-</span>
                    )}
                  </td>
                  <td className="tw-text-right">
                    <div className="tw-flex tw-gap-2 tw-justify-end tw-items-center">
                      {task.efforts && task.efforts.length > 0 && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          active={expandedRows.includes(task.id)}
                          onClick={() => toggleEfforts(task.id)}
                          title={expandedRows.includes(task.id) ? 'Hide Efforts' : 'Show Efforts'}
                        >
                          <i className={`bi bi-chevron-${expandedRows.includes(task.id) ? 'up' : 'down'}`} />
                        </Button>
                      )}
                      <TaskActionsDropdown
                        task={task}
                        canEditTask={canEditTask}
                        canCloneTask={canCloneTask}
                        canCloseTask={canCloseTask}
                        canReopenTask={canReopenTask}
                        canCancelTask={canCancelTask}
                        onViewHistory={() => onViewHistory(task)}
                        onEditTask={() => onEditTask(task)}
                        onCloneTask={() => onCloneTask(task)}
                        onCloseTask={() => onCloseTask(task)}
                        onReopenTask={() => onReopenTask(task)}
                        onCancelTask={() => onCancelTask(task)}
                        onPrintPDF={() => onPrintPDF(task)}
                        onConvertToQuantified={
                          onConvertToQuantified ? () => onConvertToQuantified(task) : undefined
                        }
                        onConvertToCustom={onConvertToCustom ? () => onConvertToCustom(task) : undefined}
                        onReportAccomplishment={
                          onReportAccomplishment ? () => onReportAccomplishment(task) : undefined
                        }
                        onSetInternalReview={
                          onSetInternalReview ? () => onSetInternalReview(task) : undefined
                        }
                        onSetActive={onSetActive ? () => onSetActive(task) : undefined}
                      />
                    </div>
                  </td>
                </tr>
                {expandedRows.includes(task.id) && (
                  <tr>
                    <td colSpan={8} className="tw-p-0">
                      <div className="tw-border-t-0 tw-p-3 tw-bg-surface-alt">
                        <TaskEffortsTable
                          efforts={task.efforts || []}
                          columns={{ poNumber: true, fuelSurcharge: true }}
                          projectMarkups={projectMarkups(task.project)}
                          canToggleVehicleFuel={canToggleVehicleFuel}
                          onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {sortedTasks.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="tw-text-center tw-text-content-muted tw-py-4">
                  <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2"></i>
                  No project tasks found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default ProjectWorksTable;
